import React from "react";
import { DateTime } from "luxon";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TimelineItem, { timelineItemClasses } from "@mui/lab/TimelineItem";
import Timeline from "@mui/lab/Timeline";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import Cached from "@mui/icons-material/Cached";
import { useMyProfile, usePublisherGameUpdate, usePublisherGameUpdates } from "@seabrookstudios/pitch2table";
import { AddPublisherUpdate } from "./AddPublisherUpdate";
import { PublisherUpdateOwnerIcon } from "./PublisherUpdateOwnerIcon";
import { EditPublisherUpdate } from "./EditPublisherUpdate";
import { CircularProgressSize } from "../../theme";

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PublisherGameUpdateId} props.updateId
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.publisherGame
 * @param {import("@seabrookstudios/types").UserId} props.userId
 * @param {boolean} props.notLast
 */
const PublisherUpdate = ({ updateId, notLast, publisherGame, userId }) => {
  const { data: update } = usePublisherGameUpdate(publisherGame.id, updateId);

  if (!update) {
    return <Skeleton />;
  }

  return (
    <TimelineItem>
      <TimelineSeparator>
        <TimelineDot />
        {notLast && <TimelineConnector />}
      </TimelineSeparator>
      <TimelineContent>
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="column" justifyContent="space-between">
            <Typography>
              {DateTime.fromMillis(update.timestamp).toFormat("DDD")}
              <Typography variant="body2" color="text.secondary" component="span">
                {DateTime.fromMillis(update.timestamp).toFormat(" @ TTT")}
              </Typography>{" "}
              <EditPublisherUpdate update={update} publisherGame={publisherGame} userId={userId} />
            </Typography>
            <Typography color="text.secondary" variant="body2" maxWidth="66ch" sx={{ whiteSpace: "pre-wrap" }}>
              {update.update}
            </Typography>
          </Stack>
          <PublisherUpdateOwnerIcon update={update} publisherGame={publisherGame} />
        </Stack>
      </TimelineContent>
    </TimelineItem>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").DesignerPublisherGameView} props.publisherGame
 */
export const PublisherInterestActivityCard = ({ publisherGame }) => {
  const { data: profile } = useMyProfile();
  const { data: updates = [], isLoading, refetch, isRefetching } = usePublisherGameUpdates(publisherGame.id);

  if (isLoading || !profile) {
    return (
      <Card elevation={1} square={false}>
        <Box p={2} justifyContent="center">
          <CircularProgress />
        </Box>
      </Card>
    );
  }

  return (
    <Card elevation={1} square={false}>
      <Box p={2}>
        <Stack pt={1} direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" textAlign="left" color="text.secondary" noWrap>
            Activity
          </Typography>
          <Stack direction="row">
            <Button onClick={() => refetch()}>
              {isRefetching ? <CircularProgress size={CircularProgressSize} /> : <Cached />}
            </Button>
            <AddPublisherUpdate publisherGame={publisherGame} userId={profile.id} />
          </Stack>
        </Stack>
        <Box pt={1}>
          <Timeline
            sx={{
              [`& .${timelineItemClasses.root}:before`]: {
                flex: 0,
                padding: 0,
              },
            }}
          >
            {updates.map((update, i) => (
              <PublisherUpdate
                key={update.id}
                updateId={update.id}
                notLast={i < updates.length - 1}
                publisherGame={publisherGame}
                userId={profile.id}
              />
            ))}
            {updates.length === 0 && <Typography>No updates yet.</Typography>}
          </Timeline>
        </Box>
      </Box>
    </Card>
  );
};
